import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";
import Overlay from "components/Overlay/Overlay";

const ServiceCostTypeNewEdit = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const {
    overlayData,
    setOverlayName,
    showSnackbar,
    handleErrorDialog,
    openDialog,
    closeDialog,
  } = useStateContext();

  const isEditMode = !!overlayData?.costTypeId;
  const allServiceCostTypes = useMemo(
    () =>
      (overlayData?.allServiceCostTypes || []).filter(
        (item) => String(item?.Name || "").toLowerCase() !== "vendor cost",
      ),
    [overlayData?.allServiceCostTypes],
  );
  const usageCount = Number(overlayData?.usageCount || 0);

  usePageTitle(isEditMode ? "Edit Service Cost Type" : "New Service Cost Type");

  const [name, setName] = useState(overlayData?.name || "");
  const [description, setDescription] = useState(overlayData?.description || "");
  const [sortOrder, setSortOrder] = useState(Number(overlayData?.sortOrder || 0));
  const [isActive, setIsActive] = useState(overlayData?.isActive !== false);
  const [defaultIncludeInTotalCost, setDefaultIncludeInTotalCost] = useState(
    overlayData?.defaultIncludeInTotalCost !== false,
  );
  const [defaultIncludeInAccountCharge, setDefaultIncludeInAccountCharge] = useState(
    Boolean(overlayData?.defaultIncludeInAccountCharge),
  );
  const [defaultIncludeInLinguistPay, setDefaultIncludeInLinguistPay] = useState(
    Boolean(overlayData?.defaultIncludeInLinguistPay),
  );
  const [visibleToLinguists, setVisibleToLinguists] = useState(
    overlayData?.visibleToLinguists === true || overlayData?.visibleToLinguists === false ? overlayData.visibleToLinguists : false
  );

  const [initialSnapshot] = useState(() => ({
    name: overlayData?.name || "",
    description: overlayData?.description || "",
    sortOrder: Number(overlayData?.sortOrder || 0),
    isActive: overlayData?.isActive !== false,
    defaultIncludeInTotalCost: overlayData?.defaultIncludeInTotalCost !== false,
    defaultIncludeInAccountCharge: Boolean(overlayData?.defaultIncludeInAccountCharge),
    defaultIncludeInLinguistPay: Boolean(overlayData?.defaultIncludeInLinguistPay),
    visibleToLinguists: overlayData?.visibleToLinguists === true || overlayData?.visibleToLinguists === false ? overlayData.visibleToLinguists : false,
  }));

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const closeOverlay = () => setOverlayName("");

  const isDuplicateName = useMemo(() => {
    const trimmed = name.trim().toLowerCase();
    if (!trimmed) return false;

    return allServiceCostTypes.some(
      (costType) =>
        (costType.Name || "").toLowerCase() === trimmed &&
        costType.id !== overlayData?.costTypeId,
    );
  }, [name, allServiceCostTypes, overlayData?.costTypeId]);

  const hasChanges = useMemo(() => {
    return (
      name.trim() !== initialSnapshot.name.trim() ||
      String(description || "") !== String(initialSnapshot.description || "") ||
      Number(sortOrder || 0) !== Number(initialSnapshot.sortOrder || 0) ||
      Boolean(isActive) !== Boolean(initialSnapshot.isActive) ||
      Boolean(defaultIncludeInTotalCost) !==
        Boolean(initialSnapshot.defaultIncludeInTotalCost) ||
      Boolean(defaultIncludeInAccountCharge) !==
        Boolean(initialSnapshot.defaultIncludeInAccountCharge) ||
      Boolean(defaultIncludeInLinguistPay) !==
        Boolean(initialSnapshot.defaultIncludeInLinguistPay) ||
      Boolean(visibleToLinguists) !== Boolean(initialSnapshot.visibleToLinguists)
    );
  }, [
    name,
    description,
    sortOrder,
    isActive,
    defaultIncludeInTotalCost,
    defaultIncludeInAccountCharge,
    defaultIncludeInLinguistPay,
    visibleToLinguists,
    initialSnapshot,
  ]);

  const emitSettingsSaved = (recordId, changeAction) => {
    if (!socket || !currentUser?.id) return;

    socket.emit("settings:saved", {
      settingsType: "ServiceCostType",
      recordId,
      changeAction,
      user: {
        id: currentUser.id,
        name: currentUser.Name,
        imageThumbnail: currentUser.ImageThumbnail,
      },
    });
  };

  const payload = {
    Name: name.trim(),
    Description: description,
    SortOrder: Number(sortOrder || 0),
    IsActive: Boolean(isActive),
    DefaultIncludeInTotalCost: Boolean(defaultIncludeInTotalCost),
    DefaultIncludeInAccountCharge: Boolean(defaultIncludeInAccountCharge),
    DefaultIncludeInLinguistPay: Boolean(defaultIncludeInLinguistPay),
    VisibleToLinguists: Boolean(visibleToLinguists),
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showSnackbar("Cost type name is required", theme, "error");
      return;
    }

    if (isDuplicateName) {
      showSnackbar("This cost type already exists", theme, "error");
      return;
    }

    if (isEditMode && !hasChanges) {
      showSnackbar("No changes to save", theme, "info");
      return;
    }

    setSaving(true);

    try {
      if (isEditMode) {
        await api.put(`/serviceCosts/types/settings/${overlayData.costTypeId}`, payload);
        emitSettingsSaved(overlayData.costTypeId, "Updated");
        showSnackbar("Service cost type updated", theme, "success");
      } else {
        const response = await api.post("/serviceCosts/types/settings", payload);
        emitSettingsSaved(response.data?.id, "Created");
        showSnackbar("Service cost type created", theme, "success");
      }

      if (typeof overlayData?.onSaved === "function") {
        await overlayData.onSaved();
      }
      closeOverlay();
    } catch (error) {
      handleErrorDialog(
        error?.response?.data?.error || "Error saving service cost type",
        theme,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!isEditMode) return;

    if (usageCount > 0) {
      showSnackbar(
        `Cannot delete. In use by ${usageCount} service${usageCount > 1 ? "s" : ""}`,
        theme,
        "error",
      );
      return;
    }

    openDialog({
      theme,
      title: "Delete Service Cost Type",
      message: `Are you sure you want to delete "${name}"?`,
      primaryAction: "Delete",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        setDeleting(true);
        try {
          await api.delete(`/serviceCosts/types/settings/${overlayData.costTypeId}`);
          closeDialog();
          emitSettingsSaved(overlayData.costTypeId, "Deleted");
          showSnackbar("Service cost type deleted", theme, "success");
          if (typeof overlayData?.onSaved === "function") {
            await overlayData.onSaved();
          }
          closeOverlay();
        } catch (error) {
          closeDialog();
          handleErrorDialog(
            error?.response?.data?.error || "Error deleting service cost type",
            theme,
          );
        } finally {
          setDeleting(false);
        }
      },
      secondaryOnClick: closeDialog,
    });
  };

  const pageTitle = isEditMode ? "Edit Service Cost Type" : "New Service Cost Type";

  const footerActions = (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", width: "100%", justifyContent: "space-between" }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {isEditMode && (
          isMobile ? (
            <IconButton
              color="error"
              onClick={handleDelete}
              disabled={deleting || usageCount > 0}
              sx={{
                width: "40px",
                height: "40px",
                border: `2px solid ${theme.palette.error.main}`,
                borderRadius: "12px",
              }}
            >
              <DeleteRoundedIcon />
            </IconButton>
          ) : (
            <Button
              loading={deleting}
              color="error"
              variant="outlined"
              size="large"
              endIcon={<DeleteRoundedIcon />}
              onClick={handleDelete}
              disabled={deleting || usageCount > 0}
              sx={{ borderRadius: "12px" }}
            >
              Delete
            </Button>
          )
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {isMobile ? (
          <Tooltip title="Discard" placement="top">
            <IconButton
              color="primary"
              onClick={closeOverlay}
              sx={{
                width: "40px",
                height: "40px",
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: "12px",
              }}
            >
              <DisabledByDefaultRoundedIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            color="primary"
            variant="outlined"
            size="large"
            onClick={closeOverlay}
            sx={{ borderRadius: "12px" }}
          >
            Discard
          </Button>
        )}

        <Tooltip
          title={isMobile && (saving || isDuplicateName) ? "Please fix errors before saving" : ""}
          placement="top"
        >
          <span>
            {isMobile ? (
              <IconButton
                color="primary"
                onClick={handleSave}
                disabled={saving || isDuplicateName}
                sx={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.background.default,
                  borderRadius: "12px",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  "&.Mui-disabled": {
                    backgroundColor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                <SaveRoundedIcon />
              </IconButton>
            ) : (
              <Button
                loading={saving}
                color="primary"
                variant="contained"
                size="large"
                endIcon={<SaveRoundedIcon />}
                onClick={handleSave}
                disabled={saving || isDuplicateName}
                sx={{ borderRadius: "12px" }}
              >
                {isEditMode ? "Update" : "Create"}
              </Button>
            )}
          </span>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Overlay
      id="service-cost-type-new-edit"
      open={true}
      onClose={closeOverlay}
      title={pageTitle}
      height="fit-content"
      footer={footerActions}
    >
      <Box
        sx={{
          flexGrow: 1,
          p: "1rem 2.3rem 1rem 2.5rem",
          height: "100%",
          width: "100%",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          "& .MuiTextField-root": {
            m: 0,
            width: "100%",
          },
        }}
      >
        {isEditMode && usageCount > 0 && (
          <Alert severity="info" sx={{ mb: 1 }}>
            This cost type is used by {usageCount} service{usageCount > 1 ? "s" : ""} and cannot be deleted.
          </Alert>
        )}

        <TextField
          fullWidth
          size="small"
          label="Cost Type Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="e.g., Additional Vendor Fee"
          error={isDuplicateName}
          helperText={isDuplicateName ? "This name already exists" : ""}
        />

        <TextField
          fullWidth
          size="small"
          label="Description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          multiline
          minRows={2}
        />

        <TextField
          fullWidth
          size="small"
          label="Sort Order"
          type="number"
          value={sortOrder}
          onChange={(event) => setSortOrder(Number(event.target.value || 0))}
          inputProps={{ min: 0 }}
        />

        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "10px",
            p: 1.25,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(isActive)}
                onChange={(event) => setIsActive(event.target.checked)}
              />
            }
            label="Active"
            sx={{
              width: "100%",
              m: 0,
              pl: 1,
              display: "flex",
              alignItems: "center",
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(defaultIncludeInTotalCost)}
                disabled={
                  Boolean(defaultIncludeInTotalCost) &&
                  !defaultIncludeInAccountCharge &&
                  !defaultIncludeInLinguistPay
                }
                onChange={(event) =>
                  setDefaultIncludeInTotalCost(event.target.checked)
                }
              />
            }
            label="Default: include in Total Cost"
            sx={{
              width: "100%",
              m: 0,
              pl: 1,
              display: "flex",
              alignItems: "center",
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(defaultIncludeInAccountCharge)}
                disabled={
                  Boolean(defaultIncludeInAccountCharge) &&
                  !defaultIncludeInTotalCost &&
                  !defaultIncludeInLinguistPay
                }
                onChange={(event) =>
                  setDefaultIncludeInAccountCharge(event.target.checked)
                }
              />
            }
            label="Default: include in Account Charge"
            sx={{
              width: "100%",
              m: 0,
              pl: 1,
              display: "flex",
              alignItems: "center",
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(defaultIncludeInLinguistPay)}
                disabled={
                  Boolean(defaultIncludeInLinguistPay) &&
                  !defaultIncludeInTotalCost &&
                  !defaultIncludeInAccountCharge
                }
                onChange={(event) =>
                  setDefaultIncludeInLinguistPay(event.target.checked)
                }
              />
            }
            label="Default: include in Total Linguist Pay"
            sx={{
              width: "100%",
              m: 0,
              pl: 1,
              display: "flex",
              alignItems: "center",
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={visibleToLinguists}
                onChange={(event) =>
                  setVisibleToLinguists(Boolean(event.target.checked))
                }
              />
            }
            label="Visible to Linguists"
            sx={{
              width: "100%",
              m: 0,
              pl: 1,
              display: "flex",
              alignItems: "center",
            }}
          />

        </Box>
      </Box>
    </Overlay>
  );
};

export default ServiceCostTypeNewEdit;
